import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Cloud,
  FolderTree,
  Link2,
  ShieldCheck,
  Loader2,
  Unplug,
} from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

export default function DriveSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/drive/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get("connected") === "true") {
      toast.success("Google Drive connected", {
        description: "New clients will now get real folders in your Drive.",
      });
      setParams({}, { replace: true });
    } else if (params.get("error")) {
      toast.error("Drive connection failed", { description: params.get("error") });
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const connect = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error("Could not start Drive connect");
      setLoading(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Drive? Existing client folders remain in your Drive; only future clients will use mock URLs.")) return;
    await api.post("/drive/disconnect");
    toast.success("Drive disconnected");
    load();
  };

  return (
    <div className="p-8 lg:p-12">
      <PageHeader
        eyebrow="08 / Integrations"
        title="Google Drive"
        subtitle="Connect your Drive once and every new client onboard auto-generates its full folder tree — no manual filing."
      />

      <div className="max-w-3xl">
        <div
          data-testid="drive-status-card"
          className="rounded-xl border border-white/10 bg-zinc-900 p-8"
        >
          <div className="flex items-start gap-5">
            <div className="h-14 w-14 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
              <Cloud className="h-7 w-7 text-zinc-300" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="font-display text-2xl font-bold">Google Drive</h2>
                {status?.connected ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-400">
                    <CheckCircle2 className="h-3 w-3" /> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-zinc-500/10 text-zinc-400">
                    Not connected
                  </span>
                )}
              </div>
              {status?.connected ? (
                <>
                  <p className="mt-2 text-sm text-zinc-400">
                    Connected as{" "}
                    <span className="text-zinc-100 font-mono">
                      {status.connected_by_email || "your Google account"}
                    </span>
                    .
                  </p>
                  {status.parent_folder_url && (
                    <a
                      data-testid="drive-parent-link"
                      href={status.parent_folder_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 font-mono"
                    >
                      <Link2 className="h-3 w-3" />
                      /Purnova All Cilent Data root folder
                    </a>
                  )}
                </>
              ) : (
                <p className="mt-2 text-sm text-zinc-400">
                  Sign in with your Google account so we can create client folders
                  and file final outputs on your behalf.
                </p>
              )}

              <div className="mt-6 flex items-center gap-3">
                {status?.connected ? (
                  <>
                    <Button
                      data-testid="drive-reconnect-btn"
                      onClick={connect}
                      disabled={loading}
                      variant="outline"
                      className="border-white/10 bg-transparent hover:bg-white/5 text-zinc-200"
                    >
                      {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Re-authorize
                    </Button>
                    <Button
                      data-testid="drive-disconnect-btn"
                      onClick={disconnect}
                      variant="outline"
                      className="border-rose-500/30 bg-transparent hover:bg-rose-500/10 text-rose-300"
                    >
                      <Unplug className="h-4 w-4 mr-2" />
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    data-testid="drive-connect-btn"
                    onClick={connect}
                    disabled={loading}
                    className="rounded-full bg-white text-zinc-950 hover:bg-zinc-200"
                  >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Cloud className="h-4 w-4 mr-2" />}
                    Connect Google Drive
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* What happens */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              icon: FolderTree,
              title: "Standardised tree",
              body: "Every new client gets 01_Raw_Footage, 02_Work_In_Progress, 03_Final_Outputs — automatically.",
            },
            {
              icon: Link2,
              title: "Auto-populated links",
              body: "Editors see the correct raw folder link the moment a task is assigned. Zero copy-paste.",
            },
            {
              icon: ShieldCheck,
              title: "Your Drive, your control",
              body: "Folders live under your own Purnova All Cilent Data root. Disconnect any time — files stay put.",
            },
          ].map((it, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-zinc-900 p-5">
              <it.icon className="h-5 w-5 text-zinc-400" />
              <div className="mt-3 font-semibold">{it.title}</div>
              <div className="mt-1.5 text-sm text-zinc-400 leading-relaxed">{it.body}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 text-xs text-amber-200/80 space-y-2">
          <div>
            <span className="font-semibold text-amber-200">Before you click Connect — Google Cloud Console setup (one-time):</span>
          </div>
          <div>
            <span className="font-semibold text-amber-200">1. Credentials → your OAuth 2.0 Client → add:</span>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li>
                <span className="text-amber-200/70">Authorized JavaScript origins:</span>{" "}
                <span className="font-mono text-amber-200">{window.location.origin}</span>
              </li>
              <li>
                <span className="text-amber-200/70">Authorized redirect URIs:</span>{" "}
                <span className="font-mono text-amber-200">{window.location.origin}/api/drive/callback</span>
              </li>
            </ul>
          </div>
          <div>
            <span className="font-semibold text-amber-200">2. OAuth consent screen → Test users → add the email of the Google account whose Drive you want to connect.</span>
            <div className="mt-1 text-amber-200/70">
              This is required only while the OAuth app is in <em>Testing</em> mode. You can add up to 100 test users. Add every email you plan to use.
            </div>
          </div>
          <div>
            <span className="font-semibold text-amber-200">3. APIs &amp; Services → Library → make sure Google Drive API is Enabled.</span>
          </div>
          <div className="pt-2 border-t border-amber-500/20 text-amber-200/70">
            When you click <span className="text-amber-200 font-semibold">Connect Google Drive</span>, Google shows an account picker — choose the account you added as a Test user. It does NOT have to be the same account you signed into AgencyOps with.
          </div>
        </div>
      </div>
    </div>
  );
}
